import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useSelectionStore } from '@/pinia/selectionNew'
import { getLevelsForBuilding } from '@/services/floors.service'
import { getRoomsForSelectedLevelv1 } from '@/services/rooms.service'
import type { ComplexFeature, RealEstateFeature, BuildingFeature } from '@/components/types'
import type { LEVEL } from '@/interfaces/level.interface'
import type { CoreEntityDto, CoreType } from '@/interfaces/core.interface'
import { LevelOfDetail, useLevelOfDetail } from './levelOfDetail'

export type Entity = {
  id: string
  name: string
  type: CoreType
  parentId?: string
  parent?: Entity
  properties?: any
  geometry?: any
}

export const useLoadedEntities = defineStore('loaded-entities', () => {
  const vuexStore = useStore()
  const selection = useSelectionStore()

  const isLoading = ref(false)
  const levelsForSelectedBuildings = ref<LEVEL[]>([])
  const levelsCache = ref<Record<string, LEVEL[]>>({})
  const { currentLevelOfDetail } = storeToRefs(useLevelOfDetail())
  const roomsForSelectedLevel = ref<Entity[]>([])
  const roomsCache = ref<Record<string, Entity[]>>({}) // moved up to handle error in console about no roomscache before initialization

  watch(
    [() => selection?.selectedEntitiesByType.CONSTRUCTION, currentLevelOfDetail],
    async ([constructions]: [CoreEntityDto[] | undefined, LevelOfDetail]) => {
      if (!constructions) {
        return
      }
      roomsForSelectedLevel.value = []
      const uniqueConstructions = [...new Map(constructions.map((b) => [b.id, b])).values()]
      isLoading.value = true
      const promises = uniqueConstructions.map(async (building) => {
        if (!levelsCache.value[building.id]) {
          const result = await getLevelsForBuilding(building.id, undefined, false, true)
          levelsCache.value[building.id] = result?.data?.features ?? []
        }
        return levelsCache.value[building.id]
      })
      const arraysOfLevels = await Promise.all(promises)
      const flattenedLevels = arraysOfLevels.flat()
      // Deduplicate levels by their id.
      const dedupedLevels = [
        ...new Map(flattenedLevels.map((lvl) => [lvl.properties.id, lvl])).values()
      ]
      levelsForSelectedBuildings.value = dedupedLevels
      loadSpaces(
        dedupedLevels.map((lvl) => ({
          id: lvl.properties.id,
          parentId: lvl.properties.constructionId
        }))
      )
      isLoading.value = false
    },
    { immediate: true }
  )

  watch(
    () => selection?.selectedEntitiesByType.LEVEL,
    async (levels: CoreEntityDto[] | undefined) => {
      if (!levels) {
        return
      }
      isLoading.value = true
      roomsForSelectedLevel.value = []
      await loadSpaces(levels)
      isLoading.value = false
    },
    { immediate: true }
  )

  async function loadSpaces(levels: { parentId?: string; id: string }[]) {
    const promises = levels.map(async (level) => {
      if (level.parentId) {
        if (roomsCache.value[level.id]) {
          return roomsCache.value[level.id]
        } else {
          const result = await getRoomsForSelectedLevelv1(level.parentId, level.id)
          const rooms = result.data.features.map((room) => ({
            id: room.id,
            name: room.properties.displayName,
            parentId: room.properties.level,
            type: 'SPACE' as CoreType,
            geometry: room.geometry,
            properties: room.properties
          }))
          roomsCache.value[level.id] = rooms
          return rooms
        }
      } else {
        return []
      }
    })
    const arraysOfRooms = await Promise.all(promises)
    const flattenedRooms = arraysOfRooms.flat()
    roomsForSelectedLevel.value = flattenedRooms
  }

  const loadedEntities = computed<Entity[]>(() => {
    const complexes: Entity[] =
      (vuexStore.getters.allComplexes?.map((x: ComplexFeature) => ({
        id: x.properties.belongsToId,
        name: x.properties.displayName,
        type: 'COMPLEX',
        properties: x.properties
      })) as Entity[]) ?? []

    const realEstates: Entity[] =
      (vuexStore.getters.allRealEstates?.map((x: RealEstateFeature) => ({
        id: x.properties.id,
        name: x.properties.displayName,
        parentId: x.properties.complexId,
        type: 'REAL_ESTATE',
        properties: x.properties,
        parent: complexes.find((c) => c.id === x.properties.complexId)
      })) as Entity[]) ?? []

    const constructions: Entity[] =
      (vuexStore.getters.allBuildings?.map((x: BuildingFeature) => ({
        id: x.properties.id,
        name: x.properties.displayName,
        parentId: x.properties.realEstateId,
        type: 'CONSTRUCTION',
        properties: x.properties,
        geometry: x.geometry,
        parent: realEstates.find((r) => r.id === x.properties.realEstateId)
      })) as Entity[]) ?? []

    const fetchedLevels: Entity[] = levelsForSelectedBuildings.value.map((lvl) => ({
      id: lvl.properties.id,
      name: lvl.properties.name,
      parentId: lvl.properties.constructionId,
      type: 'LEVEL',
      properties: lvl.properties,
      parent: constructions.find((c) => c.id === lvl.properties.constructionId)
    }))
    selection.selectedEntitiesByType.LEVEL?.forEach((selectedLevel) => {
      if (!fetchedLevels.find((lvl) => lvl.id === selectedLevel.id)) {
        fetchedLevels.push(selectedLevel)
      }
    })

    const rooms: Entity[] = roomsForSelectedLevel.value.map((room) => ({
      ...room,
      parent: fetchedLevels.find((lvl) => lvl.id === room.parentId)
    }))

    return [...complexes, ...realEstates, ...constructions, ...fetchedLevels, ...rooms]
  })

  return { loadedEntities, isLoading }
})
